import { Menu, X } from "lucide-react";
import type { ViewDefinition, ViewId } from "../lib/views.js";
import { isPlainLeftClick } from "../lib/useRoute.js";
import { cn } from "../lib/cn.js";

interface Props {
  views: readonly ViewDefinition[];
  current: ViewId;
  onNavigate: (path: string) => void;
}

/**
 * The design's centre pill rail.
 *
 * One rounded, bordered track holding the links, with the active one drawn as
 * a filled pill inside it. The design animates that pill between positions
 * with a shared `layoutId`; here it is simply the active link's own
 * background, because the slide is the one piece of motion that cannot be
 * expressed without a layout animation and it carries no information the fill
 * does not.
 *
 * Anchors with real `href`s, not the design's buttons. Buttons meant no
 * middle-click, no open-in-new-tab, no `aria-current` and nothing a reader
 * could copy out of the address bar. This is the one place the port
 * deliberately changes a role rather than preserving it.
 */
export function Navigation({ views, current, onNavigate }: Props) {
  return (
    <nav
      aria-label="Sections"
      // `lg`, not the design's `md`. Measured at 768: the brand, the rail and
      // the action rail come to 829px against a 768px viewport, so every page
      // scrolled sideways at that width -- which section 4 forbids outright.
      // The design's header carries five icons where this one carries a
      // three-state theme control as well, and something had to give. The rail
      // is the piece with a drawer to fall back to.
      className="hidden items-center gap-1 rounded-full border border-line bg-line/40 p-1 shadow-sm lg:flex"
    >
      {views.map((view) => (
        <SectionLink
          key={view.id}
          view={view}
          active={view.id === current}
          onNavigate={onNavigate}
        />
      ))}
    </nav>
  );
}

/**
 * The same links, behind a disclosure, for widths the rail does not fit.
 *
 * Whether it is open is the shell's business rather than this component's,
 * because the header has a second dropdown -- the account menu -- and the two
 * are `absolute` from the same header at overlapping positions. Owned
 * separately they could both be open, and the drawer then sat over the menu
 * and swallowed its clicks. One piece of state, one open popover.
 */
export function MobileNavigation({ views, current, onNavigate, open, onOpenChange }: DrawerProps) {
  return (
    <div className="lg:hidden">
      <button
        type="button"
        onClick={() => {
          onOpenChange(!open);
        }}
        aria-expanded={open}
        aria-controls="mobile-sections"
        className="rounded-lg p-2 text-ink-muted transition-colors hover:bg-line hover:text-ink"
      >
        <span className="sr-only">{open ? "Close menu" : "Open menu"}</span>
        {open ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
      </button>

      {open && (
        <nav
          id="mobile-sections"
          // Not "Sections": the rail is still in the document at this width,
          // hidden by a media query rather than unmounted, and two landmarks
          // sharing a name is both an axe complaint and an ambiguous locator.
          aria-label="Sections menu"
          className="absolute inset-x-0 top-full z-40 border-b border-line bg-card px-6 py-4 shadow-xl"
        >
          <ul className="flex flex-col gap-2">
            {views.map((view) => (
              <li key={view.id}>
                <SectionLink
                  view={view}
                  active={view.id === current}
                  onNavigate={(path) => {
                    onOpenChange(false);
                    onNavigate(path);
                  }}
                  block
                />
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}

interface DrawerProps extends Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface LinkProps {
  view: ViewDefinition;
  active: boolean;
  onNavigate: (path: string) => void;
  block?: boolean;
}

function SectionLink({ view, active, onNavigate, block = false }: LinkProps) {
  return (
    <a
      href={view.path}
      // The state, for anyone who cannot see which pill is filled. The design
      // conveyed it with a sliding indicator and nothing else.
      aria-current={active ? "page" : undefined}
      onClick={(event) => {
        if (!isPlainLeftClick(event)) return;
        event.preventDefault();
        onNavigate(view.path);
      }}
      className={cn(
        "whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
        block ? "block w-full px-4 py-2.5 text-left" : "inline-flex items-center",
        // A solid fill, not the design's translucent accent. Composited over
        // the page that blend is #dfe4cc and accent text on it measures 4.2:1,
        // under the 4.5 section 4 asks for, and axe failed it. This pair is
        // the one already measured for filled controls.
        active ? "bg-btn font-semibold text-btn-ink shadow-sm" : "text-ink-muted hover:text-ink",
      )}
    >
      {view.label}
    </a>
  );
}
